<template>
  <div class="ocean-ai-page">
    <div class="page-head">
      <div>
        <h2>{{ creativeDetail ? '创意维度明细' : '巨量广告 AI 数据' }}</h2>
        <p v-if="!creativeDetail">采集并查询巨量项目、广告、素材维度的小时数据、天数据和配置快照</p>
      </div>
      <div v-if="!creativeDetail" class="head-actions">
        <el-tag size="mini" :type="status.running ? 'warning' : 'success'">
          {{ status.running ? '采集中' : '采集可用' }}
        </el-tag>
        <el-button size="mini" icon="el-icon-refresh" :loading="statusLoading" @click="loadStatus">刷新状态</el-button>
      </div>
    </div>

    <section class="query-panel">
      <el-tabs v-if="!creativeDetail" v-model="activeTab" type="card" @tab-click="handleTabChange">
        <el-tab-pane v-for="tab in tabs" :key="tab.key" :label="tab.label" :name="tab.key" />
      </el-tabs>

      <el-form :inline="true" :model="query" size="mini" class="query-form">
        <el-form-item label="日期">
          <el-date-picker
            v-model="dateRange"
            type="daterange"
            value-format="yyyy-MM-dd"
            range-separator="至"
            start-placeholder="开始日期"
            end-placeholder="结束日期"
            style="width: 250px"
          />
        </el-form-item>
        <el-form-item label="账号ID">
          <el-input v-model.trim="query.accountId" clearable placeholder="输入账号ID" style="width: 170px" />
        </el-form-item>
        <el-form-item label="项目ID">
          <el-input v-model.trim="query.projectId" clearable placeholder="输入项目ID" style="width: 170px" />
        </el-form-item>
        <el-form-item v-if="isPromotionFilterVisible" :label="promotionIdLabel">
          <el-input v-model.trim="query.promotionId" clearable :placeholder="promotionIdPlaceholder" style="width: 170px" />
        </el-form-item>
        <el-form-item v-if="isMaterialTab" label="素材ID">
          <el-input v-model.trim="query.materialId" clearable placeholder="输入素材ID" style="width: 170px" />
        </el-form-item>
        <el-form-item label="投放版本">
          <el-select v-model="query.platformVersion" clearable placeholder="全部" style="width: 120px">
            <el-option label="全部" value="" />
            <el-option label="旧版" value="OLD" />
            <el-option label="智擎" value="STD" />
            <el-option label="素材汇总" value="ALL" />
          </el-select>
        </el-form-item>
        <el-form-item v-if="isReportTab">
          <el-checkbox v-model="query.excludeZeroCost">过滤消耗为0</el-checkbox>
        </el-form-item>
        <el-form-item label="关键词">
          <el-input v-model.trim="query.keyword" clearable placeholder="名称、ID、组织、错误信息" style="width: 220px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" :loading="loading" @click="search">查询</el-button>
          <el-button icon="el-icon-refresh-left" @click="resetQuery">重置</el-button>
          <el-button icon="el-icon-download" :disabled="tableData.length === 0" @click="exportCurrentPage">导出当前页</el-button>
        </el-form-item>
      </el-form>
    </section>

    <section class="summary-grid">
      <div v-for="item in summaryCards" :key="item.key" class="summary-card">
        <span>{{ item.label }}</span>
        <strong :class="item.className">{{ item.value }}</strong>
      </div>
    </section>

    <section class="table-panel">
      <div class="table-toolbar">
        <span>数据源：{{ sourceText }}</span>
        <div class="toolbar-actions">
          <el-button v-if="!creativeDetail" size="mini" icon="el-icon-refresh" :loading="syncLoading" @click="syncCurrent">同步当前条件</el-button>
          <el-popover placement="bottom" trigger="click" width="280">
            <div class="column-options">
              <el-checkbox-group v-model="visibleColumns">
                <el-checkbox v-for="column in availableColumns" :key="column.name" :label="column.name">
                  {{ column.label }}
                </el-checkbox>
              </el-checkbox-group>
            </div>
            <el-button slot="reference" size="mini" icon="el-icon-setting">列设置</el-button>
          </el-popover>
        </div>
      </div>

      <el-table
        :data="tableData"
        size="mini"
        border
        stripe
        :height="tableHeight"
        v-loading="loading"
        :header-cell-style="{ background: '#f8fafc', color: '#303133', fontWeight: 600 }"
        @sort-change="handleSortChange"
      >
        <el-table-column
          v-for="column in renderedColumns"
          :key="column.name"
          :prop="column.name"
          :label="column.label"
          :min-width="columnWidth(column.name, column.label)"
          :sortable="sortableColumn(column.name) ? 'custom' : false"
          show-overflow-tooltip
        >
          <template slot-scope="{ row }">
            <span :class="valueClass(column.name, row[column.name], row)">{{ formatCell(row[column.name], column.name, row) }}</span>
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination-row">
        <span>共 {{ formatInteger(pagination.total) }} 条</span>
        <el-pagination
          background
          layout="sizes, prev, pager, next, jumper"
          :current-page="pagination.page"
          :page-size="pagination.pageSize"
          :page-sizes="[30, 50, 100, 200, 500]"
          :total="pagination.total"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        />
      </div>
    </section>
  </div>
</template>

<script>
export default {
  name: 'OceanEngineAiData',
  props: {
    creativeDetail: { type: Boolean, default: false }
  },
  data() {
    const today = this.todayDate()
    return {
      statusLoading: false,
      loading: false,
      syncLoading: false,
      activeTab: this.creativeDetail ? 'materialHourly' : localStorage.getItem('oceanEngineAiDataTab') || 'projectHourly',
      tabs: [
        { key: 'projectHourly', label: '项目小时' },
        { key: 'projectDaily', label: '项目天' },
        { key: 'materialHourly', label: '素材小时' },
        { key: 'materialDaily', label: '素材天' },
        { key: 'projectSnapshot', label: '项目快照' },
        { key: 'promotionSnapshot', label: '广告快照' },
        { key: 'materialSnapshot', label: '素材映射' }
      ],
      dateRange: [today, today],
      query: {
        accountId: '',
        projectId: '',
        promotionId: '',
        materialId: '',
        platformVersion: '',
        keyword: '',
        excludeZeroCost: false
      },
      status: {},
      summary: {},
      sourceText: '-',
      availableColumns: [],
      visibleColumns: [],
      tableData: [],
      sort: {
        prop: '',
        order: ''
      },
      pagination: {
        page: 1,
        pageSize: Number(localStorage.getItem(this.stateKey('oceanEngineAiDataPageSize')) || 50),
        total: 0
      }
    }
  },
  computed: {
    isReportTab() {
      return ['projectHourly', 'projectDaily', 'materialHourly', 'materialDaily'].includes(this.activeTab)
    },
    isMaterialTab() {
      return ['materialHourly', 'materialDaily', 'materialSnapshot'].includes(this.activeTab)
    },
    isPromotionFilterVisible() {
      return this.creativeDetail || ['promotionSnapshot', 'materialHourly', 'materialDaily', 'materialSnapshot'].includes(this.activeTab)
    },
    promotionIdLabel() {
      return this.creativeDetail || this.isMaterialTab ? '单元ID' : '广告ID'
    },
    promotionIdPlaceholder() {
      return `输入${this.promotionIdLabel}`
    },
    renderedColumns() {
      if (!this.visibleColumns.length && !this.creativeDetail) return this.availableColumns
      return this.availableColumns.filter(column => this.visibleColumns.includes(column.name))
    },
    tableHeight() {
      return 'calc(100vh - 335px)'
    },
    summaryCards() {
      const total = this.summary || {}
      return [
        { key: 'rows', label: '匹配行数', value: this.formatInteger(total.row_count || this.pagination.total) },
        { key: 'cost', label: '总消耗', value: this.formatAmount(total.total_cost), className: 'blue' },
        { key: 'cash', label: '总现金消耗', value: this.formatAmount(total.total_cash_cost), className: 'green' },
        { key: 'time', label: '最近采集', value: this.formatTime(total.latest_collected_at), className: 'small' }
      ]
    }
  },
  mounted() {
    this.restoreQuery()
    if (!this.creativeDetail) this.loadStatus()
    this.loadData()
  },
  methods: {
    stateKey(key) {
      return this.creativeDetail ? `creativeMaterialHourly_${key}` : key
    },
    rowRevenueAttribution(row) {
      const source = String(row.revenue_attribution || '')
      if (['BILLING', '计费', '计费时间'].includes(source)) return '计费'
      if (['ACTIVATION', '激活', '激活时间'].includes(source)) return '激活'
      let metrics = row.raw_metrics_json || {}
      if (typeof metrics === 'string') {
        try {
          metrics = JSON.parse(metrics)
        } catch (e) {
          metrics = {}
        }
      }
      // Older responses lack the attribution flag; identify billing by its original metric names.
      return Object.keys(metrics || {}).some(name => /^(stat_)?attribution_billing_micro_game_/.test(name)) ? '计费' : '激活'
    },
    labelReportColumns(columns, rows) {
      if (!this.isReportTab) return columns
      const sources = rows.map(row => ({ row, attribution: this.rowRevenueAttribution(row) }))
      return columns.map(column => {
        const label = column.label || column.name
        if (/[（(](计费|激活|回传)[^）)]*[）)]$/.test(label)) return column
        const name = column.name
        let attribution
        if (name === 'pay_amount') {
          attribution = '回传'
        } else if (name === 'roi' || /^micro_game_\d+[dh]_roi$/.test(name) ||
          ['micro_game_24h_amount', 'micro_game_7d_amount'].includes(name) || this.recoveryColumn(name)) {
          if (this.isMaterialTab || ['micro_game_14d_roi', 'micro_game_30d_roi'].includes(name)) {
            attribution = '激活'
          } else {
            const populated = sources.filter(item => item.row[name] !== null && item.row[name] !== undefined && item.row[name] !== '')
            const bases = new Set((populated.length ? populated : sources).map(item => item.attribution))
            attribution = bases.size > 1 ? '计费/激活' : bases.has('激活') ? '激活' : '计费'
          }
        }
        return attribution ? Object.assign({}, column, { label: `${label}(${attribution})` }) : column
      })
    },
    async loadStatus() {
      if (this.creativeDetail) return
      this.statusLoading = true
      try {
        const response = await this.$axios.get('/oceanengine/ai-data/status')
        if (response.data && response.data.success) {
          this.status = response.data.data || {}
        }
      } finally {
        this.statusLoading = false
      }
    },
    async loadData() {
      this.loading = true
      try {
        const params = Object.assign({}, this.queryParams(), {
            page: this.pagination.page,
            pageSize: this.pagination.pageSize,
            sortProp: this.sort.prop,
            sortOrder: this.sort.order
        })
        const response = this.creativeDetail
          ? await this.$axios.post('/tf1/data/find', Object.assign({}, params, { reportType: 'materialHourly', person: '' }))
          : await this.$axios.get('/oceanengine/ai-data', { params })
        if (!response.data || !response.data.success) {
          this.$message.error((response.data && response.data.msg) || '查询失败')
          return
        }
        const data = response.data.data || {}
        this.tableData = data.list || []
        this.pagination.total = Number(data.total || 0)
        this.summary = data.summary || {}
        this.sourceText = data.source || '-'
        this.availableColumns = this.labelReportColumns(data.columns || [], this.tableData)
        this.applyColumnDefaults()
        this.saveQuery()
      } finally {
        this.loading = false
      }
    },
    async syncCurrent() {
      if (this.creativeDetail) return
      try {
        const accountId = this.query.accountId
        if (!accountId) {
          await this.$confirm('未填写账号ID时，会按报表使用中的账号批量同步，可能耗时较长。是否继续？', '同步确认', {
            confirmButtonText: '继续同步',
            cancelButtonText: '取消',
            type: 'warning'
          })
        }
      } catch (e) {
        return
      }
      this.syncLoading = true
      try {
        const params = this.queryParams()
        params.collectSnapshots = ['projectSnapshot', 'promotionSnapshot', 'materialSnapshot'].includes(this.activeTab)
        params.collectReports = this.isReportTab
        params.hourly = ['projectHourly', 'materialHourly'].includes(this.activeTab)
        params.daily = ['projectDaily', 'materialDaily'].includes(this.activeTab)
        if (!params.collectSnapshots && !params.collectReports) {
          params.collectSnapshots = true
        }
        const response = await this.$axios.post('/oceanengine/ai-data/sync', params)
        if (!response.data || !response.data.success) {
          this.$message.error((response.data && response.data.msg) || '同步失败')
          return
        }
        const data = response.data.data || {}
        this.$message.success(`同步完成：账号 ${data.accountCount || 0} 个，写入 ${data.rows || 0} 行`)
        await this.loadStatus()
        await this.loadData()
      } finally {
        this.syncLoading = false
      }
    },
    search() {
      this.pagination.page = 1
      this.loadData()
    },
    resetQuery() {
      const today = this.todayDate()
      this.dateRange = [today, today]
      this.query = {
        accountId: '',
        projectId: '',
        promotionId: '',
        materialId: '',
        platformVersion: '',
        keyword: '',
        excludeZeroCost: false
      }
      this.sort = { prop: '', order: '' }
      this.search()
    },
    handleTabChange() {
      if (this.creativeDetail) return
      localStorage.setItem('oceanEngineAiDataTab', this.activeTab)
      this.visibleColumns = []
      this.pagination.page = 1
      this.loadData()
    },
    handleSizeChange(size) {
      this.pagination.pageSize = size
      localStorage.setItem(this.stateKey('oceanEngineAiDataPageSize'), String(size))
      this.pagination.page = 1
      this.loadData()
    },
    handleCurrentChange(page) {
      this.pagination.page = page
      this.loadData()
    },
    handleSortChange({ prop, order }) {
      this.sort = { prop: prop || '', order: order || '' }
      this.loadData()
    },
    queryParams() {
      const range = Array.isArray(this.dateRange) ? this.dateRange : []
      return {
        tab: this.creativeDetail ? 'materialHourly' : this.activeTab,
        date: range[0] || this.todayDate(),
        startDate: range[0] || this.todayDate(),
        endDate: range[1] || range[0] || this.todayDate(),
        accountId: this.query.accountId,
        projectId: this.query.projectId,
        promotionId: this.isPromotionFilterVisible ? this.query.promotionId : '',
        materialId: this.isMaterialTab ? this.query.materialId : '',
        platformVersion: this.query.platformVersion,
        keyword: this.query.keyword,
        excludeZeroCost: this.query.excludeZeroCost
      }
    },
    applyColumnDefaults() {
      const key = this.stateKey('oceanEngineAiColumns_' + this.activeTab)
      const saved = JSON.parse(localStorage.getItem(key) || '[]')
      const names = this.availableColumns.map(item => item.name)
      if (this.creativeDetail) {
        const defaultsKey = this.stateKey('columnDefaultsVersion')
        const hidden = ['stat_cash_cost', 'show_cnt', 'click_cnt', 'ctr', 'convert_cnt',
          'conversion_cost', 'conversion_rate', 'revenue_attribution', 'status',
          'error_message', 'collected_at', 'material_name', 'account_name', 'organization_names']
        const defaultsVersion = localStorage.getItem(defaultsKey)
        const initialized = defaultsVersion === '2'
        const selected = localStorage.getItem(key) !== null && (defaultsVersion || saved.length) ? saved : names
        if (defaultsVersion === '1' && names.includes('promotion_id') && !selected.includes('promotion_id')) {
          selected.push('promotion_id')
        }
        this.visibleColumns = selected.filter(name => names.includes(name) && (initialized || !hidden.includes(name)))
        localStorage.setItem(key, JSON.stringify(this.visibleColumns))
        localStorage.setItem(defaultsKey, '2')
        return
      }
      if (saved.length) {
        this.visibleColumns = saved.filter(name => names.includes(name))
      } else {
        this.visibleColumns = names
      }
    },
    saveQuery() {
      localStorage.setItem(this.stateKey('oceanEngineAiDataQuery'), JSON.stringify({
        dateRange: this.dateRange,
        query: this.query
      }))
      localStorage.setItem(this.stateKey('oceanEngineAiColumns_' + this.activeTab), JSON.stringify(this.visibleColumns))
    },
    restoreQuery() {
      try {
        const saved = JSON.parse(localStorage.getItem(this.stateKey('oceanEngineAiDataQuery')) || '{}')
        if (saved.dateRange && saved.dateRange.length) {
          this.dateRange = saved.dateRange
        }
        if (saved.query) {
          this.query = Object.assign({}, this.query, saved.query)
        }
      } catch (e) {
        // ignore bad local cache
      }
    },
    exportCurrentPage() {
      const columns = this.renderedColumns
      const lines = [columns.map(column => this.csvEscape(column.label)).join(',')]
      this.tableData.forEach(row => {
        lines.push(columns.map(column => this.csvEscape(this.formatCell(row[column.name], column.name, row))).join(','))
      })
      const blob = new Blob(['\ufeff' + lines.join('\n')], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = `${this.creativeDetail ? '创意维度明细' : '巨量广告AI数据'}_${this.activeTab}_${this.todayDate()}.csv`
      link.click()
      URL.revokeObjectURL(url)
    },
    csvEscape(value) {
      const text = value === null || value === undefined ? '' : String(value)
      return `"${text.replace(/"/g, '""')}"`
    },
    formatCell(value, column, row) {
      if (this.isMissingRecoveryCell(value, column, row)) return '未采到'
      if (value === null || value === undefined || value === '') return '-'
      if (this.idColumn(column)) return String(value)
      if (column === 'platform_version') return this.platformName(value)
      if (column === 'data_topic') return this.topicName(value)
      if (this.enumColumn(column)) return this.enumName(value)
      if (this.timeColumn(column)) return this.formatTime(value)
      if (this.roiColumn(column)) return this.formatDecimal(value)
      if (this.amountColumn(column)) return this.formatAmount(value)
      if (this.integerColumn(column)) return this.formatInteger(value)
      if (this.rateColumn(column)) return this.formatDecimal(value)
      return String(value)
    },
    platformName(value) {
      const map = { OLD: '旧版', STD: '智擎', ALL: '素材汇总' }
      return map[value] || value || '-'
    },
    topicName(value) {
      const map = {
        BASIC_DATA: '基础数据',
        STD_BASIC_DATA: '智擎基础数据',
        CREATIVE_DATA: '素材数据'
      }
      return map[value] || value || '-'
    },
    enumColumn(column) {
      return [
        'marketing_goal',
        'delivery_mode',
        'delivery_type',
        'status',
        'status_first',
        'status_second',
        'pricing',
        'budget_mode',
        'deep_bid_type',
        'schedule_type',
        'material_type',
        'materials_type'
      ].includes(column)
    },
    enumName(value) {
      const text = value === null || value === undefined ? '' : String(value).trim()
      if (!text || text === '-') return '-'
      const map = {
        VIDEO_AND_IMAGE: '短视频/图文',
        LIVE: '直播',
        SEARCH: '搜索',
        PROCEDURAL: '自动投放',
        MANUAL: '手动投放',
        NORMAL: '普通投放',
        PROJECT_STATUS_ENABLE: '启用',
        PROJECT_STATUS_DISABLE: '暂停',
        PROJECT_STATUS_STOP: '暂停',
        PROJECT_STATUS_DELETE: '已删除',
        PROJECT_STATUS_DELETED: '已删除',
        PROJECT_STATUS_NOT_START: '未开始',
        PROJECT_STATUS_DONE: '已结束',
        PROJECT_STATUS_AUDIT: '审核中',
        PROJECT_STATUS_REJECT: '审核拒绝',
        PROJECT_STATUS_AUDIT_DENY: '审核拒绝',
        PROJECT_STATUS_BUDGET_EXCEED: '预算不足',
        PROJECT_STATUS_BALANCE_EXCEED: '余额不足',
        PROJECT_STATUS_PRE_ONLINE: '预上线',
        PROJECT_STATUS_NO_SCHEDULE: '不在投放时段',
        PROMOTION_STATUS_ENABLE: '启用',
        PROMOTION_STATUS_DISABLE: '暂停',
        PROMOTION_STATUS_STOP: '暂停',
        PROMOTION_STATUS_DELETE: '已删除',
        PROMOTION_STATUS_DELETED: '已删除',
        PRICING_OCPM: 'OCPM出价',
        PRICING_OCPC: 'OCPC出价',
        PRICING_CPC: 'CPC出价',
        PRICING_CPM: 'CPM出价',
        PRICING_CPV: 'CPV出价',
        PRICING_CPA: 'CPA出价',
        BUDGET_MODE_DAY: '日预算',
        BUDGET_MODE_TOTAL: '总预算',
        BUDGET_MODE_INFINITE: '不限预算',
        CUSTOM: '自定义',
        ROI_COEFFICIENT: 'ROI系数',
        FIRST_ROI_COEFFICIENT: '首日ROI系数',
        SEVEN_ROI_COEFFICIENT: '7日ROI系数',
        THIRTY_ROI_COEFFICIENT: '30日ROI系数',
        DEEP_BID_DEFAULT: '默认深度出价',
        SCHEDULE_FROM_NOW: '立即开始',
        SCHEDULE_START_END: '指定开始结束时间',
        SCHEDULE_FIXED_RANGE: '固定投放时间',
        VIDEO: '视频',
        IMAGE: '图片',
        SMART_IMAGE: '智能图片',
        SMART_VIDEO: '智能视频',
        STATIC_ASSEMBLE: '程序化创意',
        DYNAMIC_ASSEMBLE: '动态创意'
      }
      if (text[0] === '[') {
        try {
          const list = JSON.parse(text)
          if (Array.isArray(list)) {
            return list.length ? list.map(item => map[item] || item).join('、') : '-'
          }
        } catch (e) {
          // ignore invalid array text
        }
      }
      return map[text] || text
    },
    formatTime(value) {
      if (!value) return '-'
      const original = String(value).trim()
      if (original.includes('T') && /(Z|[+-]\d{2}:?\d{2})$/.test(original)) {
        const date = new Date(original)
        if (!Number.isNaN(date.getTime())) {
          return this.formatBeijingDateTime(date)
        }
      }
      const text = original.replace('T', ' ')
      return text.length >= 19 ? text.slice(0, 19) : text
    },
    formatBeijingDateTime(date) {
      const parts = new Intl.DateTimeFormat('zh-CN', {
        timeZone: 'Asia/Shanghai',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
        hour12: false
      }).formatToParts(date).reduce((map, part) => {
        map[part.type] = part.value
        return map
      }, {})
      return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}:${parts.second}`
    },
    formatLocalDateTime(date) {
      const pad = value => String(value).padStart(2, '0')
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
    },
    formatAmount(value) {
      const number = Number(value)
      if (!Number.isFinite(number)) return '-'
      return number.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 })
    },
    formatInteger(value) {
      const number = Number(value)
      if (!Number.isFinite(number)) return '-'
      return Math.round(number).toLocaleString()
    },
    formatDecimal(value) {
      const number = Number(value)
      if (!Number.isFinite(number)) return '-'
      return Number(number.toFixed(4)).toString()
    },
    todayDate() {
      const now = new Date()
      const y = now.getFullYear()
      const m = String(now.getMonth() + 1).padStart(2, '0')
      const d = String(now.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    idColumn(column) {
      return /(^id$|_id$|Id$|account_id|project_id|promotion_id|material_id|creative_id)/.test(column)
    },
    timeColumn(column) {
      return /time|date|created_at|updated_at|collected_at/.test(column)
    },
    amountColumn(column) {
      return /cost|cash|amount|budget|bid|goal|cpm|recovery/.test(column)
    },
    recoveryColumn(column) {
      return /^h([1-9]|1[0-9]|2[0-4])_recovery$/.test(column)
    },
    recoveryMarker(column) {
      const matched = String(column || '').match(/^h([1-9]|1[0-9]|2[0-4])_recovery$/)
      return matched ? `H${matched[1]}` : ''
    },
    isMissingRecoveryCell(value, column, row) {
      if (!this.recoveryColumn(column)) return false
      if (value !== null && value !== undefined && value !== '') return false
      const markers = String((row && row.recovery_missing_hours) || '')
        .split(',')
        .map(item => item.trim())
        .filter(Boolean)
      return markers.includes(this.recoveryMarker(column))
    },
    roiColumn(column) {
      return /(^roi$|_roi$|roi$)/.test(column)
    },
    integerColumn(column) {
      return /cnt|count|hour|show_cnt|click_cnt|convert_cnt/.test(column)
    },
    rateColumn(column) {
      return /rate|ctr/.test(column)
    },
    sortableColumn(column) {
      return /^h([1-9]|1[0-9]|2[0-4])_recovery$/.test(column) ||
        ['stat_cost', 'stat_cash_cost', 'show_cnt', 'click_cnt', 'convert_cnt', 'roi', 'pay_amount',
          'micro_game_24h_amount', 'micro_game_7d_amount', 'micro_game_0d_roi', 'micro_game_24h_roi',
          'micro_game_2d_roi', 'micro_game_3d_roi', 'micro_game_4d_roi', 'micro_game_5d_roi',
          'micro_game_6d_roi', 'micro_game_7d_roi', 'micro_game_14d_roi', 'micro_game_30d_roi',
          'bid', 'budget', 'collected_at', 'stat_time_hour'].includes(column)
    },
    columnWidth(column, label = '') {
      if (/[（(](计费|激活|回传)/.test(label)) return label.includes('/') ? 200 : 170
      if (this.idColumn(column)) return 130
      if (/name|organization|error|raw|schedule/.test(column)) return 220
      if (this.timeColumn(column)) return 160
      if (this.amountColumn(column)) return 120
      return 110
    },
    valueClass(column, value, row) {
      if (this.isMissingRecoveryCell(value, column, row)) return 'warning'
      if (value === null || value === undefined || value === '') return 'muted'
      if (column === 'status' && String(value) !== 'SUCCESS') return 'danger'
      if (column === 'stat_cost') return 'blue'
      if (column === 'stat_cash_cost') return 'green'
      if (/roi$/.test(column)) return Number(value) > 0 ? 'orange' : 'muted'
      if (/recovery|micro_game_.*amount/.test(column)) return Number(value) > 0 ? 'green' : 'muted'
      return ''
    }
  },
  watch: {
    visibleColumns() {
      localStorage.setItem(this.stateKey('oceanEngineAiColumns_' + this.activeTab), JSON.stringify(this.visibleColumns))
    }
  }
}
</script>

<style scoped>
.ocean-ai-page {
  min-height: 100%;
  padding: 18px;
  background: #f3f6fb;
  color: #1f2d3d;
  font-family: "PingFang SC", "Microsoft YaHei", Arial, sans-serif;
}

.page-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 10px;
}

.page-head h2 {
  margin: 0 0 6px;
  font-size: 20px;
  font-weight: 700;
}

.page-head p {
  margin: 0;
  color: #6b778c;
  font-size: 13px;
}

.head-actions,
.toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.query-panel,
.table-panel {
  background: #fff;
  border: 1px solid #dfe6f1;
  border-radius: 4px;
}

.query-panel {
  padding: 10px 12px 0;
}

.query-form {
  padding: 8px 0 2px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(160px, 1fr));
  gap: 10px;
  margin: 10px 0;
}

.summary-card {
  background: #fff;
  border: 1px solid #dfe6f1;
  border-radius: 4px;
  padding: 12px 14px;
}

.summary-card span {
  display: block;
  color: #7b8794;
  font-size: 12px;
  margin-bottom: 6px;
}

.summary-card strong {
  font-size: 20px;
  color: #1f2d3d;
}

.summary-card .small {
  font-size: 14px;
}

.table-panel {
  padding: 10px;
}

.table-toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
  color: #4a5568;
  font-size: 13px;
}

.column-options {
  max-height: 360px;
  overflow: auto;
}

.column-options .el-checkbox {
  display: block;
  margin: 0 0 8px;
}

.pagination-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 8px;
  color: #5f6b7a;
  font-size: 13px;
}

.blue {
  color: #409eff;
}

.green {
  color: #19a55a;
}

.orange {
  color: #e67e22;
}

.danger {
  color: #f56c6c;
}

.warning {
  color: #e6a23c;
}

.muted {
  color: #9aa5b1;
}
</style>
